import { Color } from '@tmh-website/shared/colors';
import { Breakpoint, breakpointToThemeKey } from '@tmh-website/shared/const';

export const theme = {
  // layout
  siteMaxWidth: '1248px', // has to be px
  siteMaxWidthLarge: '1440px',
  fullScreenMaxWidth: '2560px',
  smallSectionMaxWidth: '860px',
  sectionMaxHeight: '1200px',
  wrapperPadding: '24px',
  wrapperPaddingMobile: '24px',
  gap: {
    xSmall: '10px',
    small: '20px',
    medium: '30px',
    large: '60px',
    xLarge: '96px',
  },
  sectionSpacing: {
    desktop: {
      small: '64px',
      medium: '104px',
      large: '136px',
    },
    mobile: {
      medium: '64px',
      large: '80px',
      xLarge: '96px',
    },
  },
  btnPadding: '10.5px 28px 9.5px',
  btnPaddingMobile: '10px 24px',
  btnHoverPadding: '10.5px 29px 9.5px',
  btnHoverPaddingMobile: '11px 25px',
  btnMinHeight: '48px',
  btnSmallPadding: '9px 20px',
  btnSmallHoverPadding: '10px 21px',
  btnSmallMinHeight: '36px',
  focusBoxShadow: '0 0 0 2px',
  formElementsPadding: '14px 20px 2px',
  formElementsPaddingX: '20px',
  formElementsButtonsMinHeight: '44px',
  formElementsButtonsPadding: '10px 24px',
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '24px',
  },
  isBorderRadiusDisabled: false as boolean,
  listOfContentsWidth: '232px',
  headerAudienceTabsHeight: '34px',
  headerNavigationHeight: '72px',
  headerNavigationHeightMobile: '64px',
  headerHeight: '72px',
  headerHeightMobile: '64px',
  headerHeightCombined: '106px',
  subnavHeight: '72px',
  subnavHeightMobile: '64px',
  localeSwitchBannerHeight: '72px',
  localeSwitchBannerHeightSmallMobile: '130px',
  textOpacity: 0.6,
  transitionTiming: '0.25s ease',
  transitionTimingFunctionBouncy: 'cubic-bezier(0.57, 1.66, 0.45, 0.915)',
  salTransitionTiming: '0.75s ease',
  salTransition: 'opacity 0.75s ease, transform 0.75s ease',
  salOffset: '16px',
  salDelayInMs: 200,

  // breakpoint
  breakpoint: {
    [breakpointToThemeKey[Breakpoint.Xs]]: {
      up: `min-width: ${Breakpoint.Xs}px`,
      down: `max-width: ${Breakpoint.Xs - 0.02}px`,
    },
    [breakpointToThemeKey[Breakpoint.Sm]]: {
      up: `min-width: ${Breakpoint.Sm}px`,
      down: `max-width: ${Breakpoint.Sm - 0.02}px`,
    },
    [breakpointToThemeKey[Breakpoint.Md]]: {
      up: `min-width: ${Breakpoint.Md}px`,
      down: `max-width: ${Breakpoint.Md - 0.02}px`,
    },
    [breakpointToThemeKey[Breakpoint.Lg]]: {
      up: `min-width: ${Breakpoint.Lg}px`,
      down: `max-width: ${Breakpoint.Lg - 0.02}px`,
    },
    [breakpointToThemeKey[Breakpoint.Xl]]: {
      up: `min-width: ${Breakpoint.Xl}px`,
      down: `max-width: ${Breakpoint.Xl - 0.02}px`,
    },
  },

  // typo
  fontSize: {
    body: '14px',
    h1: '58px',
    h1Tablet: '48px',
    h1Mobile: '38px',
    h2: '46px',
    h2Tablet: '38px',
    h2Mobile: '32px',
    h3: '40px',
    h3Tablet: '32px',
    h3Mobile: '24px',
    h4: '28px',
    h4Tablet: '24px',
    h4Mobile: '21px',
    h5: '20px',
    h5Tablet: '20px',
    h5Mobile: '19px',
    h6: '15px',
    copy: '19px',
    copyTablet: '18px',
    copyMobile: '16px',
    copyBig: '24px',
    copyBigTablet: '22px',
    copyBigMobile: '19px',
    uiText: '15px',
    uiTextSmall: '13px',
    uiTextSuperSmallCss: '9px',
    statementBig: '28px',
    statementBigTablet: '26px',
    statementBigMobile: '24px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    singleLine: 1,
    small: 1.1,
    medium: 1.25,
    large: 1.6,
  },
  letterSpacing: {
    normal: 0,
    tight: '-0.02em',
  },

  // color
  color: {
    [Color.Blue_100]: '#D4E5FF',
    [Color.Blue_200]: '#A1C7FF',
    [Color.Blue_300]: '#589BFF',
    [Color.Blue_400]: '#3586FF',
    [Color.Blue_500_Ultramarine]: '#0066FF',
    [Color.Blue_600]: '#0052CD',
    [Color.Blue_700]: '#0041A3',
    [Color.Blue_800]: '#00327C',
    [Color.Blue_900]: '#002254',
    [Color.Blue_1000_Deep]: '#000022',

    [Color.Green_100]: '#D1F6E0',
    [Color.Green_200]: '#A3EDC1',
    [Color.Green_300]: '#75E5A1',
    [Color.Green_400]: '#47DC82',
    [Color.Green_500_Success]: '#19D363',
    [Color.Green_600]: '#14B558',
    [Color.Green_700_Shop]: '#0F974E',
    [Color.Green_800]: '#0A7943',
    [Color.Green_900]: '#055B39',
    [Color.Green_1000_Energy]: '#003D2E',

    [Color.Neutral_0_White]: '#FFFFFF',
    [Color.Neutral_100]: '#f8f8f8',
    [Color.Neutral_200_OffWhite]: '#F3F3F3',
    [Color.Neutral_300]: '#E4E4E4',
    [Color.Neutral_400]: '#D5D5D5',
    [Color.Neutral_500]: '#CACACA',
    [Color.Neutral_600]: '#979797',
    [Color.Neutral_700]: '#6A6A6A',
    [Color.Neutral_800]: '#4C4C4C',
    [Color.Neutral_900]: '#2D2D2D',
    [Color.Neutral_950_TechDarkGrey]: '#191919',
    [Color.Neutral_1000_Black]: '#000000',

    [Color.Purple_600]: '#4F4899',
    [Color.Purple_700]: '#393286',
    [Color.Purple_800]: '#2B237C',
    [Color.Purple_900]: '#1f1178',
    [Color.Purple_1000_Technology]: '#10064B',

    [Color.Red_100]: '#FDD9D9',
    [Color.Red_200]: '#F8A1A0',
    [Color.Red_300]: '#F57070',
    [Color.Red_400]: '#F13D3C',
    [Color.Red_500_Danger]: '#EE0908',
    [Color.Red_600]: '#D90807',
    [Color.Red_700]: '#A90606',
    [Color.Red_800]: '#830504',
    [Color.Red_900]: '#640403',
    [Color.Red_1000]: '#450302',

    [Color.Yellow_100]: '#FFF8CC',
    [Color.Yellow_200]: '#FFF194',
    [Color.Yellow_300]: '#FFE74C',
    [Color.Yellow_400]: '#FFDD00',
    [Color.Yellow_500_Warning]: '#FBD00F',
    [Color.Yellow_600]: '#F7C21D',
    [Color.Yellow_700]: '#F3B52C',
    [Color.Yellow_800_Gold]: '#EFA73A',
    [Color.Yellow_900]: '#E7911E',
    [Color.Yellow_1000_Orange]: '#E07A00',
  },
} satisfies {
  /**
   * Determines whether all elements should have `border-radius` disabled (set to `0`).
   *
   * @default false
   */
  isBorderRadiusDisabled: boolean;

  /**
   * Determines whether the text should have reduced opacity.
   * The default text opacity. Utilized in numerous locations throughout the whole project.
   *
   * @default 0.6
   * @min 0
   * @max 1
   */
  textOpacity: number;

  [key: string]: unknown;
};

type ThemeType = typeof theme;
export type { ThemeType };

// @ts-ignore-next-line
declare module 'styled-components' {
  export interface DefaultTheme extends ThemeType {
    fontFamily: {
      headlines: string;
      body: string;
    };
  }
}

export default theme;
